import { Button, Card, CardBody, Spinner, Tooltip } from '@heroui/react'
import type { FileListingItemDto } from '@qse/api'
import {
  getFile,
  queryKeys,
  useBulkMoveFiles,
  useBulkToggleFavorite,
  useCreateFolder,
  useLookupFile,
  useSearchFilesInfinite,
} from '@qse/api'
import type { ModalComponentProps } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'
import { Breadcrumb, HeroInput } from '@qse/ui'
import { type FolderDeletionEvent, truncateTextMiddle, useFolderDeletionListener } from '@qse/utils'
import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowRight, Plus, Star } from 'lucide-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useCallback } from 'react'
import { toast } from 'sonner'

interface MoveItemsModalData {
  selectedItems: FileListingItemDto[]
  currentFolderId?: string
  currentFolderPath?: string
  parentId?: string
}

interface MoveItemsModalProps extends ModalComponentProps {
  data: MoveItemsModalData
}

interface BreadcrumbItem {
  id: string
  name: string
  path?: string
}

// Parse folder path function matching folder.tsx pattern
const parseFolderPath = (path: string): BreadcrumbItem[] => {
  const items: BreadcrumbItem[] = []

  if (!path) return items

  // Split the path by '/' and filter out empty segments
  const pathSegments = path.split('/').filter(segment => segment.trim() !== '')

  // Skip the email part (first segment) and 'home' (second segment)
  // Start from index 2 to skip email and 'home'
  const folderSegments = pathSegments.slice(2)

  // Build the breadcrumb path progressively
  // We need to construct the full path including email and home for API calls
  let currentPath = pathSegments.length >= 2 ? `/${pathSegments[0]}/home` : '/home'

  folderSegments.forEach((segment, index) => {
    // Create a unique ID for each breadcrumb item
    const itemId = `folder-${index}-${segment}`

    // Build the path for this level (include full path for API calls)
    currentPath += `/${segment}`

    // Store the full path for API navigation
    items.push({
      id: itemId,
      name: segment,
      path: currentPath,
    })
  })

  return items
}

// Custom debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Validation function for folder name
const validateFolderName = (value: string) => {
  if (!value || value.trim() === '') {
    return 'Folder name cannot be empty'
  }
  if (value.length < 1) {
    return 'Folder name must be at least 1 character'
  }
  if (value.length > 255) {
    return 'Folder name cannot exceed 255 characters'
  }

  // Check for invalid characters that are not allowed in file/folder names
  const invalidChars = /[<>:"/\\|?\x00-\x1f]/g

  if (invalidChars.test(value)) {
    return 'Folder name contains invalid characters (< > : " / | ? or control characters)'
  }

  // Check for reserved names (Windows)
  const reservedNames = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])(\.|$)/i

  if (reservedNames.test(value)) {
    return 'Folder name cannot be a reserved system name'
  }

  // Check for names ending with dot or space
  if (value.endsWith('.') || value.endsWith(' ')) {
    return 'Folder name cannot end with a dot or space'
  }

  return true
}

export default function MoveItemsModal(props: MoveItemsModalProps) {
  const { onClose, data } = props
  const {
    selectedItems,
    currentFolderId: initialFolderId,
    currentFolderPath: initialFolderPath,
    parentId,
  } = data
  const { setModalHeader } = useActiveModelStore()

  // State management - separate for each tab
  const [selectedDestination, setSelectedDestination] = useState<string | null>(null)

  // Drive tab navigation state
  const [driveFolderId, setDriveFolderId] = useState<string | undefined>(
    initialFolderId || parentId,
  )
  const [driveFolderPath, setDriveFolderPath] = useState<string | undefined>(initialFolderPath)

  // Favorited tab navigation state - always starts at root
  const [favoritedFolderId, setFavoritedFolderId] = useState<string | undefined>(undefined)
  const [favoritedFolderPath, setFavoritedFolderPath] = useState<string | undefined>(undefined)

  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([])
  const [activeTab, setActiveTab] = useState<'drive' | 'favorited'>('drive')
  const [isMoving, setIsMoving] = useState(false)

  // Inline folder creation state
  const [showNewFolderInput, setShowNewFolderInput] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [isCreatingFolder, setIsCreatingFolder] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  const bulkMoveMutation = useBulkMoveFiles()
  const bulkToggleFavoriteMutation = useBulkToggleFavorite()
  const createFolderMutation = useCreateFolder()
  const queryClient = useQueryClient()

  // Debounce the folder name for API calls (500ms delay)
  const debouncedFolderName = useDebounce(newFolderName, 500)

  // Handle folder deletion events from other tabs
  const handleFolderDeleted = useCallback(
    (event: FolderDeletionEvent) => {
      console.log(
        '📁 Folder deleted in another tab, clearing selection and invalidating queries:',
        event,
      )

      // Clear selected destination if it was the deleted folder
      if (selectedDestination === event.folderId) {
        setSelectedDestination(null)
        toast.info(
          `Folder "${event.folderName}" was deleted in another tab and is no longer available`,
        )
      }

      // Invalidate move modal specific queries to refetch fresh folder data
      queryClient.invalidateQueries({
        queryKey: queryKeys.files.lists(),
      })

      // Invalidate specific queries for current folder context
      if (driveFolderId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.files.list({ parentId: driveFolderId }),
        })
      }

      if (favoritedFolderId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.files.list({ parentId: favoritedFolderId }),
        })
      }

      // Invalidate favorited folders query
      queryClient.invalidateQueries({
        queryKey: queryKeys.files.list({ location: 'FAVORITE' }),
      })
    },
    [selectedDestination, queryClient, driveFolderId, favoritedFolderId],
  )

  // Listen for folder deletion events from other tabs
  useFolderDeletionListener(handleFolderDeleted)

  // Get the current parent ID from the last breadcrumb item (current folder)
  const getCurrentParentId = useCallback(() => {
    if (activeTab === 'drive') {
      // driveFolderId represents the current folder (last item in breadcrumb)
      return driveFolderId
    } else {
      // favoritedFolderId represents the current folder (last item in breadcrumb)
      return favoritedFolderId
    }
  }, [activeTab, driveFolderId, favoritedFolderId])

  // Use useLookupFile hook for real-time conflict detection with debounced value
  const lookupParams = useMemo(() => {
    if (!debouncedFolderName.trim()) return null

    const currentParentId = getCurrentParentId()

    return {
      name: debouncedFolderName.trim(),
      parentId: currentParentId,
    }
  }, [debouncedFolderName, getCurrentParentId, activeTab, driveFolderPath, favoritedFolderPath])

  const { data: lookupResult, isLoading: isCheckingConflict } = useLookupFile(
    lookupParams || {
      name: '',
      parentId: undefined,
      parentPath: undefined,
    },
  )

  // Invalidate lookup cache when inline folder creation opens to ensure fresh checks
  useEffect(() => {
    if (showNewFolderInput) {
      // Clear any existing lookup cache when folder creation input opens
      queryClient.removeQueries({
        queryKey: queryKeys.files.lookup('', ''),
      })
    }
  }, [showNewFolderInput, queryClient])

  // Force refetch when debounced value changes to ensure fresh data
  useEffect(() => {
    if (debouncedFolderName.trim() && lookupParams && showNewFolderInput) {
      // Invalidate the specific lookup query to force a fresh request
      queryClient.invalidateQueries({
        queryKey: queryKeys.files.lookup(
          debouncedFolderName.trim(),
          driveFolderId || driveFolderPath || '',
        ),
      })
    }
  }, [
    debouncedFolderName,
    lookupParams,
    queryClient,
    driveFolderId,
    driveFolderPath,
    showNewFolderInput,
  ])

  // Check for naming conflicts and validate folder name
  useEffect(() => {
    if (!debouncedFolderName.trim()) {
      setValidationError(null)

      return
    }

    // First check for naming conflicts
    if (lookupResult?.file) {
      setValidationError('A folder with this name already exists in this directory')

      return
    }

    // Then validate the folder name format
    const validationResult = validateFolderName(debouncedFolderName)

    if (validationResult !== true) {
      setValidationError(validationResult)
    } else {
      setValidationError(null)
    }
  }, [lookupResult, debouncedFolderName])

  // Set modal header when component mounts
  useEffect(() => {
    const getItemFullName = (item: FileListingItemDto) => {
      // Extract full name from path (path contains the untruncated name)
      // The name field may already be truncated, so we use path instead
      if (item.path) {
        const pathSegments = item.path.split('/')

        return pathSegments[pathSegments.length - 1] || 'Unknown Item'
      }

      return item.name || 'Unknown Item'
    }

    const fullName = selectedItems.length === 1 ? getItemFullName(selectedItems[0]) : ''
    const displayTitle =
      selectedItems.length === 1
        ? `Move "${truncateTextMiddle(fullName, 30)}"`
        : `Move "${selectedItems.length} items"`

    setModalHeader({
      title: displayTitle,
      titleTooltip: selectedItems.length === 1 ? fullName : undefined,
      subtitle: undefined,
      showCloseButton: true,
    })
  }, [selectedItems, setModalHeader])

  // Separate API queries for Drive and Favorited tabs
  const driveSearchParams = useMemo(() => {
    if (activeTab === 'drive') {
      return {
        limit: 50,
        sortBy: 'name' as const,
        sortOrder: 'ASC' as const,
        isDir: true,
        parentId: driveFolderId,
        parentPath: driveFolderPath,
        location: !driveFolderPath ? 'HOME' : undefined,
      }
    }

    return null
  }, [activeTab, driveFolderId, driveFolderPath])

  const favoritedSearchParams = useMemo(() => {
    if (activeTab === 'favorited') {
      return {
        limit: 50,
        sortBy: 'name' as const,
        sortOrder: 'ASC' as const,
        isDir: true,
        // Fetch favorited folders from current folder or root
        parentId: favoritedFolderId,
        parentPath: favoritedFolderPath,
        location: 'FAVORITE',
      }
    }

    return null
  }, [activeTab, favoritedFolderId, favoritedFolderPath])

  // Drive folders query
  const {
    data: driveFoldersPages,
    isLoading: isLoadingDriveFolders,
    fetchNextPage: fetchNextDrivePage,
    hasNextPage: hasNextDrivePage,
    isFetchingNextPage: isFetchingNextDrivePage,
  } = useSearchFilesInfinite(
    driveSearchParams || {
      limit: 50,
      sortBy: 'name',
      sortOrder: 'ASC',
      isDir: true,
      parentId: undefined,
      parentPath: undefined,
      location: 'HOME',
    },
    { enabled: activeTab === 'drive' },
  )

  // Favorited folders query
  const {
    data: favoritedFoldersPages,
    isLoading: isLoadingFavoritedFolders,
    fetchNextPage: fetchNextFavoritedPage,
    hasNextPage: hasNextFavoritedPage,
    isFetchingNextPage: isFetchingNextFavoritedPage,
  } = useSearchFilesInfinite(
    favoritedSearchParams || {
      limit: 50,
      sortBy: 'name' as const,
      sortOrder: 'ASC' as const,
      isDir: true,
      location: 'FAVORITE',
    },
    { enabled: activeTab === 'favorited' },
  )

  // Flatten paginated data
  const driveFolders = driveFoldersPages?.pages.flatMap(page => page.data) || []
  const allFavoritedFolders = favoritedFoldersPages?.pages.flatMap(page => page.data) || []

  // Handle tab switching - preserve current folder state
  const handleTabChange = useCallback(
    (key: React.Key) => {
      const newTab = key as 'drive' | 'favorited'

      setActiveTab(newTab)
      setSelectedDestination(null)

      // Reset breadcrumb when switching tabs
      setBreadcrumb([])

      // Reset folder navigation states for the new tab
      if (newTab === 'drive') {
        // Drive tab starts at the initial folder or parent folder
        setDriveFolderId(initialFolderId || parentId)
        setDriveFolderPath(initialFolderPath)
        setFavoritedFolderId(undefined)
        setFavoritedFolderPath(undefined)
      } else {
        // Favorited tab starts at root level (all favorited folders)
        setFavoritedFolderId(undefined)
        setFavoritedFolderPath(undefined)
        setDriveFolderId(undefined)
        setDriveFolderPath(undefined)
      }
    },
    [initialFolderId, initialFolderPath, parentId],
  )

  // Initialize breadcrumb when component mounts or when folder paths change
  useEffect(() => {
    // Update breadcrumb for both Drive and Favorited tabs
    if (activeTab !== 'drive' && activeTab !== 'favorited') {
      return
    }

    // Choose current path to build from based on active tab
    const pathToUse = activeTab === 'drive' ? driveFolderPath : favoritedFolderPath

    if (!pathToUse) {
      // Both tabs show empty breadcrumb at root level
      setBreadcrumb([])

      return
    }

    // Use the same parseFolderPath function as folder.tsx
    const newBreadcrumb = parseFolderPath(pathToUse)

    // Avoid setState loop by comparing with current breadcrumb
    const breadcrumbJson = JSON.stringify(breadcrumb)
    const newBreadcrumbJson = JSON.stringify(newBreadcrumb)

    if (breadcrumbJson !== newBreadcrumbJson) {
      setBreadcrumb(newBreadcrumb)
    }
  }, [activeTab, driveFolderPath, favoritedFolderPath])

  const folders = useMemo(() => {
    const normalize = (path: string) => path.replace(/\/+/g, '/').replace(/\/$/, '')

    if (!driveFolderPath) {
      // At root level, show all folders returned by the API (filtered by location: 'HOME')
      return driveFolders.filter(folder => {
        // Don't show system folders as destinations
        if (folder.systemFolderType) {
          return false
        }

        return true
      })
    } else {
      // Show all folders that are direct children of the current folder
      return driveFolders.filter(folder => {
        const normalizedFolderPath = normalize(folder.path)
        const normalizedCurrentPath = normalize(driveFolderPath)

        // Don't show system folders as destinations
        if (folder.systemFolderType) {
          return false
        }

        // Check if this folder is a direct child of the current folder
        return (
          normalizedFolderPath.startsWith(normalizedCurrentPath + '/') &&
          normalizedFolderPath.split('/').length === normalizedCurrentPath.split('/').length + 1
        )
      })
    }
  }, [driveFolders, driveFolderPath])

  const filteredFavoritedFolders = useMemo(() => {
    const normalize = (path: string) => path.replace(/\/+/g, '/').replace(/\/$/, '')

    if (!favoritedFolderPath) {
      // At root level, show all favorited folders returned by the API
      return allFavoritedFolders.filter(folder => {
        // Don't show system folders as destinations
        if (folder.systemFolderType) {
          return false
        }

        return true
      })
    } else {
      // Show all favorited folders that are direct children of the current folder
      return allFavoritedFolders.filter(folder => {
        const normalizedFolderPath = normalize(folder.path)
        const normalizedCurrentPath = normalize(favoritedFolderPath)

        // Don't show system folders as destinations
        if (folder.systemFolderType) {
          return false
        }

        // Check if this folder is a direct child of the current folder
        return (
          normalizedFolderPath.startsWith(normalizedCurrentPath + '/') &&
          normalizedFolderPath.split('/').length === normalizedCurrentPath.split('/').length + 1
        )
      })
    }
  }, [allFavoritedFolders, favoritedFolderPath])

  // Get selected folder IDs to disable them
  const selectedFolderIds = selectedItems.filter(item => item.isDir).map(item => item.id)

  // Get the specific restriction reason for a disabled folder
  const getFolderRestrictionReason = (folder: FileListingItemDto): string | null => {
    if (selectedFolderIds.includes(folder.id)) {
      return 'Cannot move to folder into self'
    }

    // Check if it's a child folder of any selected folder
    const selectedFolders = selectedItems.filter(item => item.isDir)

    for (const selectedFolder of selectedFolders) {
      if (folder.path && selectedFolder.path && folder.path.startsWith(selectedFolder.path + '/')) {
        return 'Cannot move to child folder'
      }
    }

    return null
  }

  // Get favorited destinations
  const favoritedDestinations = useMemo(() => {
    return filteredFavoritedFolders
  }, [filteredFavoritedFolders])

  // Hover-based inline move
  const handleMoveToFolder = async (targetFolderId: string) => {
    setIsMoving(true)
    try {
      const fileIds = selectedItems.map(item => item.id)

      await bulkMoveMutation.mutateAsync({ fileIds, targetParentId: targetFolderId })
      onClose()
    } catch (_err) {
      console.error(_err)
      toast.error('Move failed')
    } finally {
      setIsMoving(false)
    }
  }

  // Move button in footer
  const handleMove = async () => {
    const currentFolderId = activeTab === 'drive' ? driveFolderId : favoritedFolderId

    if (!selectedDestination && !currentFolderId) return

    setIsMoving(true)
    try {
      const fileIds = selectedItems.map(item => item.id)

      await bulkMoveMutation.mutateAsync({
        fileIds,
        targetParentId: selectedDestination || currentFolderId,
      })
      onClose()
    } catch (_err) {
      console.error(_err)
      toast.error('Failed to move items')
    } finally {
      setIsMoving(false)
    }
  }

  // Inline folder creation handlers
  const handleShowNewFolderInput = () => {
    setShowNewFolderInput(true)
    setNewFolderName('')
    setValidationError(null)
  }

  const handleCancelNewFolder = () => {
    setShowNewFolderInput(false)
    setNewFolderName('')
    setValidationError(null)
  }

  const handleNewFolderNameChange = useCallback((value: string) => {
    setNewFolderName(value)

    // Validate the folder name
    const validationResult = validateFolderName(value)

    if (validationResult !== true) {
      setValidationError(validationResult)
    } else {
      setValidationError(null)
    }
  }, [])

  const handleSubmitCreateNewFolder = async () => {
    if (!newFolderName || newFolderName.trim() === '') {
      toast.error('Folder name cannot be empty')

      return
    }

    // Check for validation errors
    if (validationError) {
      toast.error(validationError)

      return
    }

    setIsCreatingFolder(true)
    try {
      const folderData: { name: string; parentId?: string } = {
        name: newFolderName.trim(),
      }

      // Get the current parent ID from the last breadcrumb item (current folder)
      const currentParentId = getCurrentParentId()

      // Always use the current parent ID if available (represents the last item in breadcrumb)
      if (currentParentId) {
        folderData.parentId = currentParentId
      } else {
        console.log('Creating folder in root directory (no current folder)')
      }

      const response = await createFolderMutation.mutateAsync(folderData)

      // If creating in Favorited tab, mark the folder as favorite after creation
      if (activeTab === 'favorited') {
        try {
          await bulkToggleFavoriteMutation.mutateAsync({ fileIds: [response.folder.id] })
        } catch (favoriteError) {
          console.error('Error marking folder as favorite:', favoriteError)
          // Don't show error to user as folder was created successfully
        }
      }

      // Clear input
      setNewFolderName('')
      setShowNewFolderInput(false)
      setValidationError(null)

      // Navigate to new folder for both Drive and Favorited tabs
      if (activeTab === 'drive') {
        setDriveFolder(response.folder.id, response.folder.path)
      } else if (activeTab === 'favorited') {
        setFavoritedFolder(response.folder.id, response.folder.path)
      }

      toast.success(
        `Folder "${response.folder.name}" created successfully! You can now move items here.`,
      )
    } catch (error) {
      console.error('Error creating folder:', error)
      toast.error('Failed to create folder. Please try again.')
    } finally {
      setIsCreatingFolder(false)
    }
  }

  // Check if create button should be disabled
  const isCreateDisabled = useMemo(() => {
    // If user has typed something, we need to wait for backend validation
    if (newFolderName.trim()) {
      // Button should be disabled if:
      // 1. Still checking for conflicts
      // 2. Has validation errors
      // 3. No lookup result yet (backend hasn't responded)
      // 4. Any pending operations
      return (
        !!validationError ||
        !lookupResult ||
        createFolderMutation.isPending ||
        bulkMoveMutation.isPending
      )
    }

    // If user hasn't typed anything, button is disabled
    return !newFolderName.trim() || createFolderMutation.isPending || bulkMoveMutation.isPending
  }, [
    newFolderName,
    createFolderMutation.isPending,
    bulkMoveMutation.isPending,
    validationError,
    lookupResult,
  ])

  const handleCancel = () => {
    onClose()
  }

  // Navigation functions - separate for each tab
  const setDriveFolder = (folderId: string, folderPath: string) => {
    setDriveFolderId(folderId)
    setDriveFolderPath(folderPath)
    setSelectedDestination(null) // Reset selection when navigating
    // Breadcrumb will be updated by the useEffect that watches driveFolderPath
  }

  const setFavoritedFolder = (folderId: string, folderPath: string) => {
    setFavoritedFolderId(folderId)
    setFavoritedFolderPath(folderPath)
    setSelectedDestination(null) // Reset selection when navigating
    // Breadcrumb will be updated by the useEffect that watches favoritedFolderPath
  }

  const handleBreadcrumbClick = async (item: BreadcrumbItem) => {
    if (item.id === 'root') {
      // Navigate to root for both tabs
      setDriveFolderId(undefined)
      setDriveFolderPath(undefined)
      setFavoritedFolderId(undefined)
      setFavoritedFolderPath(undefined)
      setBreadcrumb([])
    } else if (item.path) {
      // Navigate to specific folder based on path
      try {
        const folder = await getFile({ path: item.path })

        if (folder?.id) {
          if (activeTab === 'drive') {
            setDriveFolder(folder.id, item.path)
          } else {
            setFavoritedFolder(folder.id, item.path)
          }

          // Update breadcrumb to show path up to this item
          const itemIndex = breadcrumb.findIndex(b => b.id === item.id)

          if (itemIndex !== -1) {
            setBreadcrumb(breadcrumb.slice(0, itemIndex + 1))
          }
        } else {
          toast.error('Folder not found')
        }
      } catch (err: any) {
        toast.error(err?.message || 'Failed to navigate to folder')
      }
    }
    setSelectedDestination(null) // Reset selection when navigating
  }

  const handleFolderClick = (folder: FileListingItemDto) => {
    // Select the folder (don't navigate)
    setSelectedDestination(folder.id)
  }

  const handleFolderDoubleClick = (folder: FileListingItemDto) => {
    // Allow navigation in both Drive and Favorited tabs
    if (activeTab === 'drive') {
      // Navigate into the folder
      setDriveFolder(folder.id, folder.path)
    } else if (activeTab === 'favorited') {
      // Navigate into the favorited folder (same behavior as drive tab)
      setFavoritedFolder(folder.id, folder.path)
    }
  }

  // Use useRef for scrollRef
  const scrollRef = useRef<HTMLDivElement>(null)

  // Infinite scroll handler
  const handleScroll = () => {
    const el = scrollRef.current

    if (!el) return
    const threshold = 100

    if (activeTab === 'drive' && hasNextDrivePage && !isFetchingNextDrivePage) {
      if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
        fetchNextDrivePage()
      }
    } else if (activeTab === 'favorited' && hasNextFavoritedPage && !isFetchingNextFavoritedPage) {
      if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
        fetchNextFavoritedPage()
      }
    }
  }

  const renderFolderRow = (
    folder: FileListingItemDto,
    showHoverActions = true,
    selectedItemsParentIds: string[],
  ): JSX.Element => {
    const isItemsAlreadyInFolder = selectedItemsParentIds.includes(folder.id)
    const restrictionReason = isItemsAlreadyInFolder ? getFolderRestrictionReason(folder) : null

    // Check if items are already in this folder (for tooltip only)

    // Allow exploration only if folder is not selected (to prevent moving to nested structure)
    const isSelected = selectedItems.some(item => item.id === folder.id)
    const canExplore = !isSelected

    const folderContent = (
      <Card
        key={folder.id}
        isPressable={canExplore}
        className={`transition-colors w-full group ${
          selectedDestination === folder.id
            ? 'cursor-pointer bg-primary/20 border-primary'
            : isItemsAlreadyInFolder || isSelected
              ? 'cursor-not-allowed opacity-60'
              : 'cursor-pointer hover:bg-gray-700/50'
        }`}
        onDoubleClick={() => canExplore && handleFolderDoubleClick(folder)}
        onPress={() => canExplore && handleFolderClick(folder)}
      >
        <CardBody className="p-3 w-full">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <img
                alt="folder"
                className={`w-5 h-5 flex-shrink-0 ${isItemsAlreadyInFolder || isSelected ? 'opacity-50' : ''}`}
                src="/assets/svg/folder.svg"
              />
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Tooltip content={folder.name} delay={500}>
                  <span
                    className={`text-sm font-medium block ${
                      isItemsAlreadyInFolder || isSelected ? 'text-gray-500' : ''
                    }`}
                  >
                    {truncateTextMiddle(folder.name, 40)}
                  </span>
                </Tooltip>
                {folder.isFavorite && (
                  <Star
                    className={`w-4 h-4 text-yellow-400 fill-current flex-shrink-0 ${isItemsAlreadyInFolder || isSelected ? 'opacity-50' : ''}`}
                  />
                )}
              </div>
            </div>

            {/* Hover Actions - show for enabled folders or current folder with toast */}
            {showHoverActions && (
              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                {isItemsAlreadyInFolder || isSelected ? (
                  // Show navigation button only if items are already in folder (not selected)
                  isItemsAlreadyInFolder && !isSelected ? (
                    <Button
                      isIconOnly
                      className="text-xs flex-shrink-0"
                      color="default"
                      size="sm"
                      variant="light"
                      onPress={() => handleFolderDoubleClick(folder)}
                    >
                      <ArrowRight className="w-3 h-3" />
                    </Button>
                  ) : null
                ) : !isItemsAlreadyInFolder && !isSelected ? (
                  <>
                    <Button
                      className="text-xs flex-shrink-0"
                      color="primary"
                      isLoading={isMoving}
                      size="sm"
                      variant="flat"
                      onPress={() => handleMoveToFolder(folder.id)}
                    >
                      Move
                    </Button>
                    <Button
                      isIconOnly
                      className="text-xs flex-shrink-0"
                      color="default"
                      size="sm"
                      variant="light"
                      onPress={() => handleFolderDoubleClick(folder)}
                    >
                      <ArrowRight className="w-3 h-3" />
                    </Button>
                  </>
                ) : (
                  <Button
                    isIconOnly
                    className="text-xs flex-shrink-0"
                    color="default"
                    size="sm"
                    variant="light"
                    onPress={() => handleFolderDoubleClick(folder)}
                  >
                    <ArrowRight className="w-3 h-3" />
                  </Button>
                )}
              </div>
            )}

            {/* Selected state - show Move button or Already Here for current folder */}
            {selectedDestination === folder.id && !showHoverActions && (
              <>
                {isItemsAlreadyInFolder || isSelected ? (
                  <Button
                    className="text-xs flex-shrink-0"
                    color="default"
                    size="sm"
                    variant="flat"
                    onPress={() =>
                      toast.info(
                        isSelected
                          ? "You can't move folder to itself or its nested folders"
                          : 'Items are already in this folder',
                      )
                    }
                  >
                    {isSelected ? "Can't Move Here" : 'Already Here'}
                  </Button>
                ) : !isItemsAlreadyInFolder && !isSelected ? (
                  <Button
                    className="text-xs flex-shrink-0"
                    color="primary"
                    isLoading={isMoving}
                    size="sm"
                    variant="flat"
                    onPress={handleMove}
                  >
                    Move
                  </Button>
                ) : null}
              </>
            )}
          </div>
        </CardBody>
      </Card>
    )

    // Wrap with tooltip if items are already in this folder or if it's the selected folder
    if (isItemsAlreadyInFolder || isSelected) {
      const tooltipMessage = isSelected
        ? "You can't move folder to itself or its nested folders"
        : 'Items are already in this folder'

      return (
        <Tooltip
          key={folder.id}
          showArrow
          className="z-50"
          content={tooltipMessage}
          delay={300}
          offset={8}
          placement="top"
        >
          {folderContent}
        </Tooltip>
      )
    }

    // Wrap with tooltip if folder is disabled and has a restriction reason
    if ((isItemsAlreadyInFolder || isSelected) && restrictionReason) {
      return (
        <Tooltip
          key={folder.id}
          showArrow
          className="z-50"
          content={restrictionReason}
          delay={300}
          offset={8}
          placement="top"
        >
          {folderContent}
        </Tooltip>
      )
    }

    return folderContent
  }

  const renderFolderList = (folders: FileListingItemDto[], selectedItemsParentIds: string[]) => {
    const isLoading = activeTab === 'drive' ? isLoadingDriveFolders : isLoadingFavoritedFolders

    if (isLoading) {
      return (
        <div className="text-center py-8 text-gray-400">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2" />
          <p>Loading folders...</p>
        </div>
      )
    }
    if (folders.length === 0) {
      return (
        <div className="text-center py-8 text-gray-400">
          <img
            alt="folder"
            className="w-12 h-12 mx-auto mb-2 opacity-50"
            src="/assets/svg/folder.svg"
          />
          <p>No folders available</p>
        </div>
      )
    }

    return (
      <div
        ref={scrollRef}
        className="space-y-2 w-full overflow-y-auto min-h-64 max-h-80 p-0.5"
        style={{ minHeight: 200 }}
        onScroll={handleScroll}
      >
        {folders.map(folder => renderFolderRow(folder, true, selectedItemsParentIds))}

        {/* Infinite scroll loading indicator */}
        {((activeTab === 'drive' && isFetchingNextDrivePage) ||
          (activeTab === 'favorited' && isFetchingNextFavoritedPage)) && (
          <div className="flex justify-center py-2 text-gray-400">Loading more folders...</div>
        )}
        {((activeTab === 'drive' && !hasNextDrivePage) ||
          (activeTab === 'favorited' && !hasNextFavoritedPage)) &&
          ((activeTab === 'drive' && folders.length > 0) ||
            (activeTab === 'favorited' && favoritedDestinations.length > 0)) && (
            <div className="flex justify-center py-2 text-xs text-gray-400">No more folders</div>
          )}
      </div>
    )
  }

  const renderTabContent = (selectedItemsParentIds: string[]) => {
    if (activeTab === 'drive') {
      return renderFolderList(folders, selectedItemsParentIds)
    } else if (activeTab === 'favorited') {
      return renderFolderList(filteredFavoritedFolders, selectedItemsParentIds)
    }

    return null
  }

  // Check if any selectedItems have parentId that matches any folder.id
  const selectedItemsParentIds = selectedItems
    .map(item => item.parentId)
    .filter(Boolean) as string[]

  // Keyboard shortcut: Enter to move
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle Enter key
      if (e.key !== 'Enter') return

      // Don't trigger if new folder input is shown (it has its own Enter handler)
      if (showNewFolderInput) return

      // Don't trigger if user is typing in an input field
      const target = e.target as HTMLElement

      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }

      // Allow Enter on the main action buttons (Cancel/Move) in the footer to work normally
      // But intercept Enter on folder cards and their inline action buttons
      if (target.tagName === 'BUTTON') {
        const buttonText = target.textContent?.trim().toLowerCase()

        // Only skip for main footer buttons
        if (buttonText === 'cancel' || buttonText === 'move') {
          return
        }
        // For all other buttons (folder cards, inline actions), continue to trigger Move
      }

      // Check if move button would be disabled
      const currentFolderId = activeTab === 'drive' ? driveFolderId : favoritedFolderId
      const isMoveDisabled = Boolean(
        (!selectedDestination && !currentFolderId) ||
          selectedItemsParentIds.includes(selectedDestination as string) ||
          selectedItemsParentIds.includes(currentFolderId as string) ||
          (selectedDestination && selectedItems.some(item => item.id === selectedDestination)) ||
          (!selectedDestination &&
            currentFolderId &&
            selectedItems.some(item => item.id === currentFolderId)),
      )

      // Don't trigger if move is disabled or already in progress
      if (isMoveDisabled || isMoving) return

      // Prevent default behavior and trigger move
      e.preventDefault()
      e.stopPropagation()
      handleMove()
    }

    // Use capture phase to intercept before card components handle it
    window.addEventListener('keydown', handleKeyDown, true)

    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [
    showNewFolderInput,
    selectedDestination,
    activeTab,
    driveFolderId,
    favoritedFolderId,
    selectedItems,
    selectedItemsParentIds,
    isMoving,
    handleMove,
  ])

  const renderNewFolderInput = () => {
    if (!showNewFolderInput) return null

    return (
      <AnimatePresence>
        {/* Backdrop with blur effect - covers the entire modal */}
        <motion.div
          animate={{ opacity: 1 }}
          className="absolute inset-0 bg-black/50  z-40 "
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleCancelNewFolder}
        />

        {/* New folder drawer - slides up from bottom */}
        <motion.div
          animate={{ y: 0, opacity: 1 }}
          className="bg-white dark:bg-[#282828] absolute bottom-0 left-0 right-0 border-t dark:text-white text-black border-gray-700 p-6 z-50 rounded-t-2xl"
          exit={{ y: '100%', opacity: 0 }}
          initial={{ y: '100%', opacity: 0 }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30,
            duration: 0.4,
          }}
        >
          {/* Title */}
          <h3 className="text-lg font-semibold mb-4">New folder</h3>
          <div className="flex gap-3 items-start">
            <div className="flex-1 my-auto">
              <div className="space-y-2">
                <HeroInput
                  isRequired
                  control="text"
                  description={newFolderName.length + '/255 characters'}
                  errorMessage={validationError}
                  isInvalid={!!validationError}
                  label="Folder Name"
                  maxLength={255}
                  name="folderName"
                  placeholder="Enter folder name"
                  radius="sm"
                  size="md"
                  value={newFolderName}
                  variant="bordered"
                  endContent={
                    <>
                      <div className="flex items-center gap-2">
                        {isCheckingConflict && <Spinner color="primary" size="sm" />}
                      </div>
                    </>
                  }
                  onChange={handleNewFolderNameChange}
                  onKeyPress={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') {
                      handleSubmitCreateNewFolder()
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <div className="flex gap-3 flex-shrink-0 my-auto justify-end">
            <Button
              className="text-blue-500 bg-transparent hover:bg-transparent"
              color="default"
              isDisabled={isCreatingFolder}
              variant="light"
              onPress={handleCancelNewFolder}
            >
              Cancel
            </Button>
            <Button
              className="bg-blue-600 text-white hover:bg-blue-700"
              color="primary"
              isDisabled={isCreateDisabled}
              isLoading={isCreatingFolder}
              variant="solid"
              onPress={handleSubmitCreateNewFolder}
            >
              Create
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumbs - show for both Drive and Favorited tabs */}

      <Breadcrumb
        isModal={true}
        items={breadcrumb}
        loading={isLoadingDriveFolders || isLoadingFavoritedFolders}
        onItemClick={handleBreadcrumbClick}
      />

      {/* New Folder Button - show when not creating and in Drive or Favorited tab */}
      {/* {!showNewFolderInput && (
        <Button
          className="w-full mb-4"
          color="primary"
          startContent={<Plus className="w-4 h-4" />}
          variant="bordered"
          onPress={handleShowNewFolderInput}
        >
          New Folder
        </Button>
      )} */}

      {/* Tab Selector - Custom implementation for full keyboard accessibility */}
      <div
        aria-label="Move destination tabs"
        className="flex gap-1 p-1 my-2 bg-default-100 rounded-lg w-fit"
        role="tablist"
      >
        <button
          aria-controls="drive-panel"
          aria-selected={activeTab === 'drive'}
          id="drive-tab"
          role="tab"
          tabIndex={0}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
            activeTab === 'drive'
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-default-600 hover:text-default-900 dark:hover:text-default-100'
          }`}
          onClick={() => handleTabChange('drive')}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleTabChange('drive')
            }
          }}
        >
          Drive
        </button>
        <button
          aria-controls="favorited-panel"
          aria-selected={activeTab === 'favorited'}
          id="favorited-tab"
          role="tab"
          tabIndex={0}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
            activeTab === 'favorited'
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-default-600 hover:text-default-900 dark:hover:text-default-100'
          }`}
          onClick={() => handleTabChange('favorited')}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleTabChange('favorited')
            }
          }}
        >
          Favorited
        </button>
      </div>

      {/* Tab Content */}
      <div
        aria-labelledby={activeTab === 'drive' ? 'drive-tab' : 'favorited-tab'}
        className="flex-1"
        id={activeTab === 'drive' ? 'drive-panel' : 'favorited-panel'}
        role="tabpanel"
        tabIndex={0}
      >
        {renderTabContent(selectedItemsParentIds)}
      </div>

      {/* Conditional Footer: Show folder creation OR footer actions */}
      {showNewFolderInput ? (
        /* Folder Creation Input - replaces footer actions */
        renderNewFolderInput()
      ) : (
        /* Footer Actions - only show when not creating folder */

        <div className="flex items-center justify-between pt-4 border-gray-700 border-t mt-4">
          {!showNewFolderInput && (
            <Tooltip content="New Folder">
              <Button
                isIconOnly
                className="font-medium"
                color="primary"
                size="sm"
                startContent={<Plus className="w-4 h-4" />}
                variant="solid"
                onPress={handleShowNewFolderInput}
              />
            </Tooltip>
          )}
          <div className="flex gap-3 justify-end">
            <Button
              aria-label="Cancel and close modal"
              isDisabled={isMoving}
              variant="flat"
              onPress={handleCancel}
            >
              Cancel
            </Button>
            <Button
              aria-label="Move selected items to destination"
              color="primary"
              isLoading={isMoving}
              isDisabled={Boolean(
                (!selectedDestination &&
                  !(activeTab === 'drive' ? driveFolderId : favoritedFolderId)) ||
                  selectedItemsParentIds.includes(selectedDestination as string) ||
                  selectedItemsParentIds.includes(
                    activeTab === 'drive'
                      ? (driveFolderId as string)
                      : (favoritedFolderId as string),
                  ) ||
                  // Check if trying to move to a selected folder
                  (selectedDestination &&
                    selectedItems.some(item => item.id === selectedDestination)) ||
                  // Check if trying to move to current folder (when no destination selected)
                  (!selectedDestination &&
                    (activeTab === 'drive' ? driveFolderId : favoritedFolderId) &&
                    selectedItems.some(
                      item =>
                        item.id === (activeTab === 'drive' ? driveFolderId : favoritedFolderId),
                    )),
              )}
              onPress={handleMove}
            >
              Move
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
